import type { RoundAggregate } from "@engine/round/aggregate";

/**
 * Response header the round-aggregate endpoint uses to carry the round's log
 * revision: the server's destructive-edit fence (count and latest id of the
 * round's audited destructive edits). Two downloads with the same revision
 * were served from the same action-log state; an in-place log edit changes
 * the revision without moving the version. The client records it on its
 * cache row and the cache-first gate compares it to the server's current
 * one (see RoundAggregateCacheRecord.logRevision).
 */
export const AGGREGATE_LOG_REVISION_HEADER = "X-Aggregate-Log-Revision";

export function formatAggregateLogRevision(fence: {
  count: number;
  latestId: string | null;
}): string {
  return `${fence.count}:${fence.latestId ?? "none"}`;
}

/** A round download together with the log revision it was served under. */
export type DownloadedRound = {
  aggregate: RoundAggregate;
  logRevision: string | null;
};

/**
 * What a round download function may resolve to: the bare aggregate (tests,
 * older callers) or the aggregate with its log revision.
 */
export type RoundDownload = RoundAggregate | DownloadedRound;

export function normalizeRoundDownload(
  download: RoundDownload,
): DownloadedRound {
  return "aggregate" in download && "logRevision" in download
    ? download
    : { aggregate: download, logRevision: null };
}
